import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronDown } from "lucide-react";
import ClientBackground from "../components/ClientBackground";

export default function FAQs() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      category: "About Tifl & Getting Started",
      questions: [
        {
          q: "What is Tifl and how does it help me as a parent?",
          a: "Tifl is a parenting companion for the first 1,000 days. It helps you track your child's milestones, understand development, get health guidance, join workshops, and connect with trusted experts."
        },
        {
          q: "Is Tifl suitable for first-time parents?",
          a: "Yes! Tifl is made especially for new and first-time parents in Pakistan who want simple, reliable, and local guidance."
        },
        {
          q: "Can I use Tifl during pregnancy?",
          a: "Yes. Tifl supports you from pregnancy to your child's second birthday."
        },
        {
          q: "Is Tifl free to use?",
          a: "The basic version is free. Some advanced features may have a small fee."
        }
      ]
    },
    {
      category: "Medical Reliability & Health",
      questions: [
        {
          q: "Is the information in Tifl medically reliable?",
          a: "All health content is created with guidance from certified pediatricians, child psychologists, and ECD specialists following global standards adapted for Pakistani families."
        },
        {
          q: "Does Tifl replace a doctor?",
          a: "No. Tifl gives guidance but does not replace medical consultation."
        },
        {
          q: "Can I upload photos of symptoms for AI health guidance?",
          a: "Yes. Tifl's Health AI lets you upload photos like rashes or poop for an initial assessment. It's private and secure."
        },
        {
          q: "Who are the experts behind Tifl?",
          a: "Tifl partners with pediatricians, psychologists, lactation consultants, and ECD specialists."
        }
      ]
    },
    {
      category: "Features & Tracking",
      questions: [
        {
          q: "How does the milestone tracker work?",
          a: "You enter your child's age, and Tifl shows you age-appropriate developmental milestones. You can log progress and get alerts if something needs attention."
        },
        {
          q: "How accurate are the milestone alerts?",
          a: "Milestones follow global standards and are adapted to Pakistani child-rearing practices."
        },
        {
          q: "What if my child is delayed in a milestone?",
          a: "Tifl guides you on whether to monitor at home or contact a doctor."
        },
        {
          q: "Can I track more than one child?",
          a: "Yes, you can create multiple child profiles."
        },
        {
          q: "What kind of workshops does Tifl offer?",
          a: "Online sessions on parenting, breastfeeding, newborn care, mental health, and ECD."
        }
      ]
    },
    {
      category: "For All Parents",
      questions: [
        {
          q: "Does Tifl work for both mom and dad?",
          a: "Yes. Tifl supports mothers, fathers, grandparents, and guardians."
        },
        {
          q: "Does Tifl help with mental load and overstimulation?",
          a: "Yes. Tifl includes tips and calming tools for overwhelmed parents."
        },
        {
          q: "I'm a busy parent. Will Tifl be easy to use?",
          a: "Yes, Tifl's interface is designed to be simple, visual, and mobile-friendly."
        }
      ]
    },
    {
      category: "Privacy & Data Security",
      questions: [
        {
          q: "Is my data safe?",
          a: "Absolutely. Your information and photos are kept confidential and never shared without permission."
        }
      ]
    },
    {
      category: "Language & Accessibility",
      questions: [
        {
          q: "What languages does Tifl support?",
          a: "Tifl is currently available in English and Urdu. We plan to expand to more regional languages in the future."
        },
        {
          q: "Can rural parents use Tifl?",
          a: "Tifl works on any smartphone and supports easy-to-understand Urdu for wider accessibility."
        },
        {
          q: "Do I need internet to use Tifl?",
          a: "Most features work online; some saved content may be accessible offline."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">FAQs</h1>
          <div className="w-10" />
        </div>

        <div className="px-5 py-6 max-w-4xl mx-auto pb-20">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Find answers to common questions about Tifl
            </p>
          </div>

          <div className="space-y-6">
            {faqData.map((category, catIndex) => (
              <div key={catIndex} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">
                    {category.category}
                  </h3>
                </div>

                <div className="divide-y divide-gray-100">
                  {category.questions.map((item, qIndex) => {
                    const index = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === index;

                    return (
                      <div key={qIndex}>
                        <button
                          onClick={() => toggleQuestion(catIndex, qIndex)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-800 pr-4">
                            {item.q}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isOpen && (
                          <div className="px-6 pb-4 pt-2">
                            <p className="text-gray-600 leading-relaxed">
                              {item.a}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-white rounded-2xl p-6 shadow-sm text-center">
            <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-4">
              Contact our support team for personalized assistance
            </p>
            <a
              href="mailto:support@tifl.com"
              className="inline-block px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#152e4a] transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}